import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GreenTimeLogo } from '../components/Layout';
import { api } from '../services/api';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', password:'', phone:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // v96: honor ?redirect= so flows like the invite/join-code pages land the
  // person back where they were instead of always dumping them on /browse.
  // Only ever used for internal app paths — never passed to an external URL.
  const redirectTo = searchParams.get('redirect') || '/browse';

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(form.email);
        setForgotSent(true);
      } else if (mode === 'login') {
        await login(form.email, form.password);
        navigate(redirectTo);
      } else {
        await register({
          firstName: form.firstName, lastName: form.lastName,
          email: form.email, password: form.password, phone: form.phone,
        });
        navigate(redirectTo);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f1eb', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <GreenTimeLogo size={48} />
            <h1 style={{ fontSize: 28, color: '#0d2b1a', fontFamily: "'Playfair Display', serif" }}>
              Green<span style={{ color: '#2e7d4f' }}>time</span>
            </h1>
          </div>
          <p style={{ fontSize: 13, color: '#7a7a6a' }}>Share tee times with your crew</p>
        </div>

        <div className="card">
          {mode !== 'forgot' && (
            <div style={{ display: 'flex', gap: 3, background: '#f4f1eb',
                          borderRadius: 7, padding: 3, marginBottom: '1rem' }}>
              {['login','register'].map(m => (
                <button key={m} onClick={() => { setMode(m); setError(''); setForgotSent(false); }}
                  style={{ flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 12, fontWeight: 500,
                           cursor: 'pointer', border: 'none', fontFamily: "'DM Sans', sans-serif",
                           background: mode === m ? '#0d2b1a' : 'transparent',
                           color: mode === m ? '#7ec99a' : '#5a5a4a' }}>
                  {m === 'login' ? 'Sign in' : 'Create account'}
                </button>
              ))}
            </div>
          )}

          {mode === 'forgot' && (
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: 16, color: '#0d2b1a', marginBottom: 4 }}>Reset your password</h3>
              <p style={{ fontSize: 12, color: '#7a7a6a' }}>Enter your email and we'll send you a reset link.</p>
            </div>
          )}

          {forgotSent ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📧</div>
              <p style={{ fontSize: 14, color: '#0d2b1a', fontWeight: 500 }}>Check your email</p>
              <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 4 }}>
                We sent a reset link to {form.email}. It expires in 1 hour.
              </p>
              <button onClick={() => { setMode('login'); setForgotSent(false); setForm(f => ({ ...f, email: '' })); }}
                style={{ marginTop: 16, fontSize: 12, color: '#2e7d4f', background: 'none', border: 'none', cursor: 'pointer' }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              {mode === 'register' && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First name</label>
                    <input className="form-input" value={form.firstName} onChange={set('firstName')} placeholder="Jane" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last name</label>
                    <input className="form-input" value={form.lastName} onChange={set('lastName')} placeholder="Smith" required />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" required />
              </div>
              {mode === 'register' && (
                <div className="form-group">
                  <label className="form-label">Mobile (optional)</label>
                  <input className="form-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(509) 555-0100" />
                </div>
              )}
              {mode !== 'forgot' && (
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="password" value={form.password} onChange={set('password')}
                         placeholder="••••••••" minLength={8} required />
                </div>
              )}
              {error && <p className="form-error">{error}</p>}
              <button type="submit" className="btn btn-accent" style={{ width: '100%', marginTop: '0.5rem', justifyContent: 'center' }} disabled={loading}>
                {loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : mode === 'forgot' ? 'Send reset link' : 'Create account'}
              </button>
              {mode === 'login' && (
                <button type="button" onClick={() => { setMode('forgot'); setError(''); }}
                  style={{ width: '100%', marginTop: 10, fontSize: 12, color: '#7a7a6a',
                           background: 'none', border: 'none', cursor: 'pointer' }}>
                  Forgot your password?
                </button>
              )}
              {mode === 'forgot' && (
                <button type="button" onClick={() => { setMode('login'); setError(''); }}
                  style={{ width: '100%', marginTop: 10, fontSize: 12, color: '#7a7a6a',
                           background: 'none', border: 'none', cursor: 'pointer' }}>
                  Back to sign in
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
